import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) {
      toast.error('Por favor, complete todos los campos');
      return;
    }
    setLoading(true);
    try {
      await login(identifier, password);
      toast.success('Bienvenido a Suite Ministerial');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex flex-1 relative bg-[#062B36] items-center justify-center p-12">
        <div className="relative z-10 text-center max-w-lg">
          <img
            src="https://customer-assets.emergentagent.com/job_pastor-dashboard-2/artifacts/w9o2gudq_LOGO%20Iglesia%20Blanco%203.png"
            alt="Tu Iglesia App"
            className="h-32 w-auto mx-auto mb-8"
          />
          <h2 className="text-3xl font-semibold text-white mb-3">Suite Ministerial</h2>
          <p className="text-white/60 text-lg">Administra miembros, eventos y ministerios con claridad y orden.</p>
          <p className="text-[#00CCFF]/80 text-sm mt-6">Tu aliado en el Ministerio de Cristo</p>
        </div>
        <div className="absolute inset-0" style={{background: 'linear-gradient(135deg, rgba(0,204,255,0.12) 0%, rgba(5,106,132,0.06) 55%, rgba(0,0,0,0) 100%)'}} />
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_pastor-dashboard-2/artifacts/pvtvhzy7_LOGO%20Iglesia%201.png"
              alt="Tu Iglesia App"
              className="h-20 w-auto mx-auto mb-4"
            />
            <h2 className="text-xl font-semibold text-foreground">Suite Ministerial</h2>
          </div>

          <Card className="shadow-lg border-border/50">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl">Bienvenido</CardTitle>
              <CardDescription>Inicia sesión para acceder al panel</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">Correo electrónico</Label>
                  <Input
                    id="identifier"
                    type="email"
                    placeholder="tu@correo.com"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    data-testid="login-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Tu contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="h-11 pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                      title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#056A84] transition-colors"
                      data-testid="login-toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#056A84] hover:bg-[#045A70] text-white btn-lift"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 spinner mr-2" /> : null}
                  {loading ? 'Iniciando...' : 'Iniciar sesión'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Tu Iglesia App &copy; {new Date().getFullYear()} &mdash; Tu aliado en el Ministerio de Cristo
          </p>
        </div>
      </div>
    </div>
  );
}
